import { envConfig } from '#constants/envConfig';
import { localStorage as localStorageBridge } from '#utils/localStorage';

/**
 * Bridge to the native DeviceSettingsPlugin (see android/.../DeviceSettingsPlugin.java)
 * used by the push-reliability onboarding. All calls are gated on IS_NATIVE, so the
 * web build tree-shakes the @capacitor/core import away.
 */

export interface DeviceInfo {
  manufacturer: string;
  ignoringBatteryOptimizations: boolean;
  /** Whether the OS will show this app's notifications at all — the one reading that is
   *  meaningful below Android 13, where the runtime permission doesn't exist yet.
   *  Undefined when the app runs against an older build of the native plugin. */
  notificationsEnabled?: boolean;
  /** Standard SYSTEM_ALERT_WINDOW grant. ⚠️ On MIUI this stays false even when the user HAS
   *  granted the OEM's own "pop-up windows" toggles — they are separate permissions, so this
   *  can confirm the setup but never disprove it. */
  canDrawOverlays?: boolean;
  /** Installed versionCode. MIUI revokes its own grants on update, so a confirmation only
   *  counts for the build it was given on. */
  appVersion?: number;

  /**
   * Снимок состояния телефона. Всё необязательное — старая нативная часть этих полей не шлёт,
   * и «не знаем» обязано отличаться от «нет».
   *
   * Заведено, чтобы перестать решать вслепую: доля людей на Android 13+, где разрешение на
   * уведомления вообще существует, была неизвестна, а от неё зависит, спрашивать человека или
   * молча забрать токен.
   */
  sdkInt?: number;
  /** «Ограничить работу в фоне»: единственное состояние, при котором систему не поднимет и пуш. */
  bgRestricted?: boolean;
  /** Бакет простоя, 45 = RESTRICTED — туда система отправляет забытые приложения. */
  standbyBucket?: number;
  /** Может ли звонок накрыть экран блокировки. `undefined` до Android 14, где запрета нет. */
  fsiAllowed?: boolean;
}

interface DeviceSettingsPlugin {
  getInfo(): Promise<DeviceInfo>;
  requestIgnoreBatteryOptimizations(): Promise<void>;
  openAutoStartSettings(): Promise<void>;
  openAppSettings(): Promise<void>;
  openNotificationSettings(): Promise<void>;
  openOtherPermissions(): Promise<void>;
  dismissCallNotification(options: { callId: string }): Promise<void>;
  setCallWindowFlags(options: { enabled: boolean }): Promise<void>;
}

let cached: DeviceSettingsPlugin | null | undefined;
let loading: Promise<void> | undefined;

/**
 * Loads the plugin proxy once and stashes it in `cached`. Returns `Promise<void>` —
 * NEVER the proxy itself.
 *
 * A Capacitor `registerPlugin` proxy is a thenable trap: it intercepts every property
 * access, so reading `.then` yields a function. If the proxy is ever a promise's
 * resolution value (e.g. `return proxy` from an async fn, or `await proxy`), the runtime
 * treats it as a thenable and calls `proxy.then(...)`, which bridges to a native method
 * named "then" that doesn't exist → "DeviceSettings.then() is not implemented on android",
 * and the whole call fails before reaching native. So we only ever access it
 * synchronously and call its real methods (which return real promises).
 */
function ensurePlugin(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        // Assign the proxy to the module var synchronously — never return or await it
        // (see the thenable-trap note above); the promise resolves to void.
        cached = registerPlugin<DeviceSettingsPlugin>('DeviceSettings');
      } catch {
        cached = null;
      }
    })();
  }
  return loading;
}

export async function getDeviceInfo(): Promise<DeviceInfo | null> {
  await ensurePlugin();
  if (!cached) {
    return null;
  }
  try {
    return await cached.getInfo();
  } catch {
    return null;
  }
}

export async function requestIgnoreBatteryOptimizations(): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.requestIgnoreBatteryOptimizations();
  } catch {
    // user cancelled / not available
  }
}

/**
 * Открывает страницу приложения в системных настройках — там же живут разрешения, а с ними и
 * микрофон.
 *
 * <p>Единственный выход из тупика: WebView, которому один раз отказали в микрофоне, больше не
 * покажет запрос сам, и до этой правки человеку оставалось предложить путь из четырёх шагов
 * текстом. Веб-версии метод недоступен по определению — там `cached` равен null, вызов молча
 * ничего не делает, и подсказка с путём остаётся единственным вариантом.
 */
export async function openAppSettings(): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.openAppSettings();
  } catch {
    // Экран недоступен — подсказка с путём остаётся на месте, человек дойдёт руками.
  }
}

export async function openAutoStartSettings(): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.openAutoStartSettings();
  } catch {
    // not available on this OEM
  }
}

/** Opens this app's system notification settings — the only way back once the notification
 *  permission is permanently denied (Android 13+) and the request dialog no longer appears. */
export async function openNotificationSettings(): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.openNotificationSettings();
  } catch {
    // not available
  }
}

/**
 * Opens the OEM screen where an incoming call is allowed to cover the display — on Xiaomi,
 * "Other permissions" with "display pop-up windows" and "show on lock screen". There is no API
 * to grant these, and without them the call rings while the phone stays asleep.
 */
export async function openOtherPermissions(): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.openOtherPermissions();
  } catch {
    // older shell build / not available
  }
}

/**
 * Releases (or re-takes) the right to show above the lock screen. Set when a call deep link
 * launches the app; cleared when the call ends, so the rest of the app doesn't stay readable
 * over the keyguard afterwards.
 */
export async function setCallWindowFlags(enabled: boolean): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.setCallWindowFlags({ enabled });
  } catch {
    // older shell build
  }
}

/**
 * Removes the system incoming-call screen for one call. Called once the in-app overlay is
 * showing the same ring (a phone with a frozen WebView gets both the WS frame and the push),
 * and after answering or declining from the notification.
 */
export async function dismissCallNotification(callId: string): Promise<void> {
  await ensurePlugin();
  if (!cached || !callId) {
    return;
  }
  try {
    await cached.dismissCallNotification({ callId });
  } catch {
    // older shell build / nothing to dismiss
  }
}

// OEMs that aggressively kill background apps → also show the autostart step.
const AGGRESSIVE_OEMS = [
  'xiaomi',
  'redmi',
  'poco',
  'huawei',
  'honor',
  'oppo',
  'realme',
  'vivo',
  'iqoo',
  'oneplus',
  'meizu',
  'samsung',
  'tecno',
  'infinix',
];

export const isAggressiveOem = (manufacturer: string): boolean =>
  AGGRESSIVE_OEMS.some((m) => manufacturer.toLowerCase().includes(m));

// Subset of aggressive OEMs that actually ship a dedicated per-app *autostart* screen.
// Samsung/Tecno/Infinix are aggressive about battery but have no autostart toggle — there
// openAutoStartSettings falls through to the app-details screen, so showing an "autostart"
// step would just duplicate the battery step. On those, the reliability tier is battery-only.
const AUTOSTART_OEMS = [
  'xiaomi',
  'redmi',
  'poco',
  'huawei',
  'honor',
  'oppo',
  'realme',
  'vivo',
  'iqoo',
  'oneplus',
  'meizu',
];

export const hasAutoStartScreen = (manufacturer: string): boolean =>
  AUTOSTART_OEMS.some((m) => manufacturer.toLowerCase().includes(m));

// Skins that refuse a call's full-screen intent until their own "display pop-up windows" /
// "show on lock screen" toggle is granted. Confirmed on MIUI (Mi 10T Pro: the call rang in the
// shade while the phone stayed asleep, appops logged the refusal); ColorOS, FuntouchOS and EMUI
// ship the same toggle on the same kind of screen, so they are listed too and will be confirmed
// when a device turns up.
//
// Samsung, Pixel and other near-stock builds are deliberately absent: there the full-screen
// intent works with no extra grant, and `canDrawOverlays` reads false for almost every app —
// listing them would nag everyone about a permission they do not need.
const LOCK_SCREEN_PERMISSION_OEMS = [
  'xiaomi',
  'redmi',
  'poco',
  'oppo',
  'realme',
  'oneplus',
  'vivo',
  'iqoo',
  'huawei',
  'honor',
  'meizu',
];

export const hasLockScreenPermission = (manufacturer: string): boolean =>
  LOCK_SCREEN_PERMISSION_OEMS.some((m) => manufacturer.toLowerCase().includes(m));

/** Confirmation is stored per installed build — an update is when MIUI revokes the grants. */
export const callScreenConfirmedKey = (appVersion: number) => `push-callscreen-done:${appVersion}`;

/**
 * Экран автозапуска у OEM. Сам шаг «разрешить запуск в фоне» из шторки убран: измерено, что
 * MIUI снимает этот грант на каждом обновлении, а на доставку пушей он не влияет — убитый
 * процесс поднимают Play Services. Открыть экран по-прежнему можно из настроек приложения,
 * поэтому `openAutoStartSettings` остаётся.
 */

/**
 * What kind of phone this is, and whether an incoming call can actually show itself on it.
 * Sent with the push-token registration to feed push_device_total — the only way to see how
 * much of the audience sits on skins that block the call screen, since debugging happens on
 * whatever handset is at hand.
 */
/**
 * Что рассказать о телефоне вместе с регистрацией токена.
 *
 * Едет попутно, без отдельного запроса, и ложится в строку токена. Снимок состояния (версия
 * Android, режим батареи, бакет простоя, включены ли уведомления, разрешён ли экран звонка)
 * нужен затем, чтобы решения про уведомления перестали приниматься на ощупь: до сих пор мы не
 * знали даже, какая доля людей сидит на Android 13+.
 */
export async function getPushDeviceSignal(): Promise<{
  deviceOem?: string;
  callScreenReady?: boolean;
  appVersion?: number;
  sdkInt?: number;
  batteryExempt?: boolean;
  bgRestricted?: boolean;
  standbyBucket?: number;
  notificationsEnabled?: boolean;
  fsiAllowed?: boolean;
}> {
  if (!envConfig.IS_NATIVE) {
    return {};
  }
  const info = await getDeviceInfo();
  if (!info) {
    return {};
  }
  const snapshot = {
    deviceOem: info.manufacturer,
    appVersion: info.appVersion,
    sdkInt: info.sdkInt,
    batteryExempt: info.ignoringBatteryOptimizations,
    bgRestricted: info.bgRestricted,
    standbyBucket: info.standbyBucket,
    notificationsEnabled: info.notificationsEnabled,
    fsiAllowed: info.fsiAllowed,
  };
  if (!hasLockScreenPermission(info.manufacturer)) {
    // Stock-ish Android: the full-screen intent needs no extra grant.
    return { ...snapshot, callScreenReady: true };
  }
  return {
    ...snapshot,
    callScreenReady:
      info.canDrawOverlays === true ||
      localStorageBridge.checkItem(callScreenConfirmedKey(info.appVersion ?? 0)),
  };
}
